"use client";

import {
  Home,
  Inbox as InboxIcon,
  Circle,
  FolderKanban,
  Settings,
  Plug,
  Plus,
  Search,
  ChevronDown,
  ChevronRight,
  Layers,
  Sparkles,
  FileText,
  LineChart,
  Download,
  Activity,
  Share2,
  Globe,
  StickyNote,
  Send,
  ListTodo,
  LogOut,
  MessageSquare,
  Loader2,
  Trash2,
  X,
} from "lucide-react";
import { createClient as createSupabaseBrowser } from "@/lib/supabase/client";
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useStore } from "@/lib/store";
import { cn } from "@/lib/utils";
import { roleCanAdmin, roleCanWrite, workspaceRoleForUser } from "@/lib/workspaceMembers";
import { TASK_NAV_QUERY_KEY } from "@/lib/taskFilters";
import { Avatar } from "./ui/Avatar";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import { CreateClientModal } from "./CreateClientModal";
import { PautaLockup } from "./brand/PautaMark";
import { TeamDock } from "./SidebarTeamDock";
import { SavedViewFavorites } from "./views/SavedViews";
import { SidebarLiveCard } from "./SidebarLiveCard";
import { liveRunSignal } from "@/lib/liveAgentRuns";

interface Props {
  activeView: string;
  inboxUnreadCount?: number | null;
  onNavigate: (view: string) => void;
  activeTeamId: string | null;
  onSelectTeam: (teamId: string, view: string) => void;
  onExport: () => void;
  onOpenTrash: () => void;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  collapsed?: boolean;
  onOpenOnboarding: () => void;
}

export function Sidebar({
  activeView,
  inboxUnreadCount,
  onNavigate,
  activeTeamId,
  onSelectTeam,
  onExport,
  onOpenTrash,
  mobileOpen = false,
  onCloseMobile,
  collapsed = false,
  onOpenOnboarding,
}: Props) {
  const users = useStore((s) => s.users);
  const activeWorkspaceId = useStore((s) => s.activeWorkspaceId);
  const workspaceCategory = useStore(
    (s) => s.workspaces.find((w) => w.id === s.activeWorkspaceId)?.category ?? "client"
  );
  const allTeams = useStore((s) => s.teams);
  const currentUserId = useStore((s) => s.currentUserId);
  const workspaceMembers = useStore((s) => s.workspaceMembers);
  const allFeedback = useStore((s) => s.feedback);
  const allLinks = useStore((s) => s.workspaceLinks);
  const allRuns = useStore((s) => s.agentRuns);
  const triageNewCount = useMemo(
    () =>
      allFeedback.filter(
        (f) => f.targetWorkspaceId === activeWorkspaceId && f.status === "new"
      ).length,
    [allFeedback, activeWorkspaceId]
  );
  const hasInbound = useMemo(
    () => allLinks.some((l) => l.targetWorkspaceId === activeWorkspaceId),
    [allLinks, activeWorkspaceId]
  );
  const hasOutbound = useMemo(
    () => allLinks.some((l) => l.sourceWorkspaceId === activeWorkspaceId),
    [allLinks, activeWorkspaceId]
  );
  const setCreateOpen = useStore((s) => s.setCreateOpen);
  const setCommandOpen = useStore((s) => s.setCommandOpen);
  const requestAgentsModal = useStore((s) => s.requestAgentsModal);
  const me = users.find((u) => u.id === currentUserId);
  const currentRole = workspaceRoleForUser(
    workspaceMembers,
    activeWorkspaceId,
    currentUserId
  );
  const canWrite = roleCanWrite(currentRole);
  const canAdmin = roleCanAdmin(currentRole);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState<string | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  useEffect(() => { if (activeView === "live") setMoreOpen(true); }, [activeView]);
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);
  const [signalNow, setSignalNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setSignalNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 768px)");
    const syncViewport = () => setIsDesktop(media.matches);
    syncViewport();
    media.addEventListener("change", syncViewport);
    return () => media.removeEventListener("change", syncViewport);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseMobile?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, onCloseMobile]);

  const navigationHidden =
    isDesktop === null ? false : isDesktop ? collapsed : !mobileOpen;

  async function handleSignOut() {
    setSigningOut(true);
    setSignOutError(null);
    const sb = createSupabaseBrowser();
    const { error } = await sb.auth.signOut({ scope: "local" });
    if (error) {
      setSignOutError(
        "Pauta couldn't sign out this browser session. Check your connection and try again."
      );
      setSigningOut(false);
      return;
    }
    if (typeof window !== "undefined") {
      window.localStorage.removeItem("pauta:activeWorkspaceId");
      window.location.assign("/login");
    }
  }

  function createNewClient() {
    if (!canWrite) return;
    setClientModalOpen(true);
  }

  const teams = useMemo(
    () => allTeams.filter((t) => t.workspaceId === activeWorkspaceId),
    [allTeams, activeWorkspaceId]
  );
  const liveRunCount = useMemo(
    () =>
      allRuns.filter(
        (run) =>
          run.workspaceId === activeWorkspaceId &&
          run.status === "in_progress" && liveRunSignal(run, signalNow) !== "stale"
      ).length,
    [activeWorkspaceId, allRuns, signalNow]
  );

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <button
          onClick={onCloseMobile}
          className="md:hidden fixed inset-0 z-30 bg-black/50"
          aria-label="Close menu"
        />
      )}
      <aside
        id="workspace-navigation"
        aria-label="Workspace navigation"
        inert={navigationHidden}
        aria-hidden={navigationHidden || undefined}
        data-navigation-hidden={navigationHidden ? "true" : "false"}
        className={cn(
          "shrink-0 h-full flex flex-col bg-[var(--bg-secondary)]",
          // Desktop: in-flow column, width animates between 244px and 0 when
          // the user toggles `collapsed` from the TopBar. We hide the border
          // and overflow when collapsed so nothing peeks through during the
          // transition.
          "md:relative md:translate-x-0",
          collapsed
            ? "md:w-0 md:border-r-0 md:overflow-hidden"
            : "md:w-[244px] md:border-r md:border-[var(--border)]",
          "md:transition-[width] md:duration-200 md:ease-out",
          // Mobile: fixed overlay slides in from the left
          "fixed inset-y-0 left-0 z-40 w-[280px] max-w-[85vw] border-r border-[var(--border)]",
          "transition-transform duration-200 ease-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
      <div className="px-3 pt-5 pb-3 flex items-center justify-center relative">
        <button onClick={onCloseMobile} aria-label="Close navigation" className="md:hidden absolute right-2 top-3 h-11 w-11 flex items-center justify-center rounded-md hover:bg-[var(--bg-hover)]"><X size={18} /></button>
        <PautaLockup
          size={40}
          compact
          className="text-[var(--fg)]"
        />
      </div>
      <div className="px-3 pb-2 flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <WorkspaceSwitcher />
        </div>
        {canWrite && (
          <button
            className="w-6 h-6 rounded-md hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)] transition-colors"
            onClick={() => setCreateOpen(true)}
            title="New task (C)"
          >
            <Plus size={14} />
          </button>
        )}
      </div>

      <div className="px-3 pb-2">
        <button
          onClick={() => setCommandOpen(true)}
          className="w-full min-h-11 md:min-h-0 flex items-center gap-2 px-2 py-1.5 rounded-md text-[12.5px] text-[var(--fg-tertiary)] bg-[var(--bg-tertiary)] hover:bg-[var(--bg-hover)] border border-[var(--border)] transition-colors"
        >
          <Search size={13} />
          <span>Search</span>
          <span className="ml-auto flex items-center gap-0.5">
            <kbd className="kbd">⌘</kbd>
            <kbd className="kbd">K</kbd>
          </span>
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto scrollbar-thin px-2">
        <Section>
          <NavItem icon={<Home size={14} />} label="Home" active={activeView === "home"} onClick={() => onNavigate("home")} view="home" />
          <NavItem icon={<InboxIcon size={14} />} label="Inbox" badge={inboxUnreadCount || undefined} badgeLabel={inboxUnreadCount ? `${inboxUnreadCount} notificações pessoais não lidas` : undefined} active={activeView === "inbox" || activeView === "notifications"} onClick={() => onNavigate("inbox")} view="inbox" />
          <NavItem icon={<Circle size={14} />} label="Minhas tarefas" active={activeView === "my-issues"} onClick={() => onNavigate("my-issues")} view="my-issues" />
          <NavItem icon={<Activity size={14} />} label="Activity" active={activeView === "activity"} onClick={() => onNavigate("activity")} view="activity" />
          <NavItem icon={<FolderKanban size={14} />} label="Projects" active={activeView === "projects"} onClick={() => onNavigate("projects")} view="projects" />
          <NavItem icon={<FileText size={14} />} label="Docs" active={activeView === "docs"} onClick={() => onNavigate("docs")} view="docs" />
          <NavItem icon={<ListTodo size={14} />} label="Tasks" active={activeView === "tasks"} onClick={() => onNavigate("tasks")} view="tasks" />
        </Section>

        <div className="mt-4">
          <div className="px-2 pb-1 text-[11px] uppercase tracking-wide text-[var(--fg-muted)] font-medium">
            For you
          </div>
          <Section>
            <NavItem icon={<ListTodo size={14} />} label="Pendências" active={activeView === "pending"} onClick={() => onNavigate("pending")} view="pending" />
          </Section>
        </div>

        <div className="mt-4">
          <button
            onClick={() => setMoreOpen((open) => !open)}
            className="focus-ring flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-1 text-[12px] text-[var(--fg-tertiary)] transition-colors hover:bg-[var(--bg-hover)] hover:text-[var(--fg-secondary)]"
            aria-expanded={moreOpen}
          >
            {moreOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <span>Operação e mais</span>
          </button>
          <AnimatePresence initial={false}>
            {moreOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="overflow-hidden pt-1"
              >
                <Section>
          <SidebarLiveCard
            activeCount={liveRunCount}
            active={activeView === "live"}
            href={`?${TASK_NAV_QUERY_KEY}=live#live`}
            onNavigate={() => onNavigate("live")}
          />
                  {(hasInbound || workspaceCategory === "dev") && (
                    <NavItem icon={<ListTodo size={14} />} label="Triage" badge={triageNewCount || undefined} active={activeView === "triage"} onClick={() => onNavigate("triage")} view="triage" />
                  )}
                  {hasOutbound && (
                    <NavItem icon={<Send size={14} />} label="Feedback outbox" active={activeView === "feedback-outbox"} onClick={() => onNavigate("feedback-outbox")} view="feedback-outbox" />
                  )}
                  <NavItem icon={<LineChart size={14} />} label="Progress" active={activeView === "progress"} onClick={() => onNavigate("progress")} view="progress" />
                  <NavItem icon={<StickyNote size={14} />} label="Notes" active={activeView === "notes"} onClick={() => onNavigate("notes")} view="notes" />
                  <NavItem icon={<Globe size={14} />} label="Shared" active={activeView === "shared"} onClick={() => onNavigate("shared")} view="shared" />
                  <NavItem icon={<Sparkles size={14} />} label="Agent runs" active={activeView === "agent-runs"} onClick={() => onNavigate("agent-runs")} view="agent-runs" />
                  <NavItem icon={<MessageSquare size={14} />} label="Chat" active={activeView === "chat"} onClick={() => onNavigate("chat")} view="chat" />
                  <NavItem icon={<Share2 size={14} />} label="Graph" active={activeView === "graph"} onClick={() => onNavigate("graph")} view="graph" />
                  <NavItem icon={<Layers size={14} />} label="Views" active={activeView === "views"} onClick={() => onNavigate("views")} view="views" />
                  {canWrite && (
                    <NavItem
                      icon={<Trash2 size={14} />}
                      label="Trash"
                      onClick={onOpenTrash}
                    />
                  )}
                </Section>

              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <SavedViewFavorites
          workspaceId={activeWorkspaceId}
          currentUserId={currentUserId}
        />
      </nav>

      {workspaceCategory !== "personal" && teams.length > 0 && (
        <TeamDock
          teams={teams}
          currentUserId={currentUserId}
          activeTeamId={activeTeamId}
          activeView={activeView}
          label={workspaceCategory === "dev" ? "Areas" : "Teams"}
          canCreate={canWrite}
          onCreate={createNewClient}
          onSelectTeam={onSelectTeam}
        />
      )}

      <div className="p-2 border-t border-[var(--border)]">
        <div className="relative">
          <button
            onClick={() => {
              setSignOutError(null);
              setAccountMenuOpen((v) => !v);
            }}
            className="w-full min-h-11 md:min-h-0 flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[var(--bg-hover)] transition-colors"
            title="Account"
          >
            <Avatar user={me} size={22} />
            <div className="flex-1 text-left min-w-0">
              <div className="text-[12.5px] font-medium truncate">{me?.name}</div>
              <div className="text-[11px] text-[var(--fg-muted)] truncate">{me?.email}</div>
            </div>
            <Settings size={13} className="text-[var(--fg-tertiary)]" />
          </button>

          {accountMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-30"
                onClick={() => setAccountMenuOpen(false)}
              />
              <div className="absolute bottom-full left-0 right-0 mb-1 z-40 rounded-md border border-[var(--border-strong)] bg-[var(--surface-elevated)] shadow-2xl py-1">
                <button
                  onClick={() => {
                    setAccountMenuOpen(false);
                    onExport();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--bg-hover)] text-[12.5px] text-[var(--fg-secondary)] text-left transition-colors"
                >
                  <Download size={12} className="text-[var(--fg-tertiary)]" />
                  Export to Obsidian
                </button>
                {canAdmin && (
                  <button
                    onClick={() => {
                      setAccountMenuOpen(false);
                      requestAgentsModal();
                    }}
                    className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--bg-hover)] text-[12.5px] text-[var(--fg-secondary)] text-left transition-colors"
                  >
                    <Settings size={12} className="text-[var(--fg-tertiary)]" />
                    Workspace settings
                  </button>
                )}
                <a
                  href="/settings/connections"
                  onClick={() => setAccountMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--bg-hover)] text-[12.5px] text-[var(--fg-secondary)] text-left"
                >
                  <Plug size={12} className="text-[var(--fg-tertiary)]" />
                  Connected apps
                </a>
                <button
                  onClick={() => {
                    setAccountMenuOpen(false);
                    onOpenOnboarding();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--bg-hover)] text-[12.5px] text-[var(--fg-secondary)] text-left transition-colors"
                >
                  <Sparkles size={12} className="text-[var(--fg-tertiary)]" />
                  Take the Pauta tour
                </button>
                <button
                  onClick={() => void handleSignOut()}
                  disabled={signingOut}
                  className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-[var(--red)]/10 disabled:opacity-50 text-[12.5px] text-[var(--red)] text-left transition-colors"
                >
                  {signingOut ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <LogOut size={12} />
                  )}
                  {signingOut ? "Signing out…" : "Sign out"}
                </button>
                {signOutError && (
                  <div
                    className="mx-2 my-1 rounded-md border border-[var(--red)]/30 bg-[var(--red)]/10 px-2 py-1.5 text-[10.5px] leading-relaxed text-[var(--red)]"
                    role="alert"
                  >
                    {signOutError}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      <CreateClientModal
        open={clientModalOpen}
        onClose={() => setClientModalOpen(false)}
      />
    </aside>
    </>
  );
}

function Section({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-0.5">{children}</div>;
}

function NavItem({
  icon,
  label,
  badge,
  badgeLabel,
  active,
  onClick,
  view,
  indent,
  accentDot,
}: {
  icon?: React.ReactNode;
  label: string;
  badge?: number | string;
  badgeLabel?: string;
  active?: boolean;
  onClick?: () => void;
  // When provided, the row renders as an <a> so Cmd+click /
  // right-click "Open in New Tab" work natively. Workspace.tsx listens
  // for this explicit navigation marker. Starting a fresh query string keeps
  // stale task filters or entity drawers from overriding the target view.
  view?: string;
  indent?: number;
  accentDot?: string;
}) {
  const className = cn(
    "group flex items-center gap-2 w-full min-h-11 md:min-h-0 rounded-md py-1 pr-2 text-[12.5px] transition-colors no-underline text-inherit",
    active
      ? "bg-[var(--bg-active)] text-[var(--fg)]"
      : "text-[var(--fg-secondary)] hover:bg-[var(--bg-hover)] hover:text-[var(--fg)]"
  );
  const style = { paddingLeft: 8 + (indent || 0) * 16 };
  const inner = (
    <>
      {accentDot && (
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: accentDot }} />
      )}
      {icon && (
        <span className="text-[var(--fg-tertiary)] group-hover:text-[var(--fg-secondary)] shrink-0">
          {icon}
        </span>
      )}
      <span className="truncate flex-1 text-left">{label}</span>
      {badge != null && (
        <span aria-label={badgeLabel} title={badgeLabel} className="text-[10.5px] text-[var(--fg-tertiary)] bg-[var(--bg-tertiary)] rounded px-1.5 py-0.5 font-medium">
          {badge}
        </span>
      )}
    </>
  );
  if (view) {
    return (
      <a
        href={`?${TASK_NAV_QUERY_KEY}=${encodeURIComponent(view)}#${encodeURIComponent(view)}`}
        onClick={(e) => {
          // Modifier-clicks (Cmd/Ctrl/Shift) and middle-click → browser's
          // native new-tab/window. Plain click → preventDefault + onClick
          // so the SPA handler runs (sets activeView, closes mobile
          // drawer) without the hash being added to the back-stack.
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
          e.preventDefault();
          onClick?.();
        }}
        className={className}
        style={style}
      >
        {inner}
      </a>
    );
  }
  return (
    <button onClick={onClick} className={className} style={style}>
      {inner}
    </button>
  );
}
